// Listen mode: Breeze listening in on a call held somewhere else, for the
// operator. There is no video call here. It is a one-off room with two seats
// and no media session:
//
//   host seat   the operator's own keyed socket. Its microphone is his side.
//   guest seat  a second socket with no key, named for the other person. The
//               tab or system audio he shares from the other app is their side.
//
// Each seat runs its own useAssistant, so each one's audio goes up the same
// binary path a real call uses, attributed to its own seat. The Room keeps one
// speech stream per seat and puts the lines in the transcript under that seat's
// name: speakers are told apart by which stream the sound came from, never by
// guessing from one mixed signal. The Room's assistant then ticks exactly as it
// does in a call, and the panel shows what it finds.
//
// The session recording is the browser's own (lib/listenRecorder.ts): the
// share's video and both sides' sound, sent to the Worker in pieces. The Room
// marks the room as a listen room for the operator only (worker/src/index.ts
// `listenRoomAllowed`), which keeps the call recorder off.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ROOM_CLOSE_CODES,
  listenGuestName,
  newOnceCode,
  LISTEN_BEAT_MS,
  LISTEN_PARAM,
  type AccountUser,
  type ClientMessage,
  type ListenBeatReply,
  type ListenLine,
  type ListenSaved,
  type ListenTarget,
  type RoomMessage,
} from "@breeze/core";
import { api, ApiError } from "../lib/api";
import { getOrCreateDeviceId, getUserKey } from "../lib/storage";
import { PING_ASSISTANT_MS, PING_MS, RoomSocket, roomWsUrl } from "../lib/wsClient";
import { openMicrophone, shareOtherSide, stopTrack } from "../lib/listenMedia";
import { ListenRecorder } from "../lib/listenRecorder";
import { useAssistant, type AssistantStore } from "./useAssistant";
import { usePanel, type PanelStore } from "./usePanel";

export type ListenPhase = "starting" | "live" | "reconnecting" | "ended" | "failed";

export interface SideState {
  /** "idle": nothing yet. "asking": the picker is open. "on": audio is coming
   * in. "lost": it was on and stopped. "off": the browser refused. */
  readonly status: "idle" | "asking" | "on" | "lost" | "off";
  readonly track: MediaStreamTrack | null;
  /** A plain line to show under the side, or null. */
  readonly message: string | null;
}

export type RecordingState = "waiting" | "on" | "paused" | "trouble";

/** The words for the status line, from what is actually happening: heard only
 * means a side is shared, so it says so, and recording is named only while
 * pieces are reaching the Worker. */
export function listenWord(store: Pick<ListenStore, "paused" | "mine" | "theirs" | "recording">): string {
  if (store.paused) return "Paused";
  if (store.theirs.status !== "on") return "Their side is not shared";
  if (store.mine.status !== "on") return "Recording them only";
  if (store.recording === "trouble") return "Recording, but it is not saving";
  return "Recording";
}

export interface ListenStore {
  readonly phase: ListenPhase;
  /** Why the session failed, when it did. */
  readonly error: string | null;
  readonly hostName: string;
  readonly guestName: string;
  readonly mine: SideState;
  readonly theirs: SideState;
  readonly panel: PanelStore;
  /** The operator's seat: the one whose switch, agenda and say-next line show. */
  readonly assistant: AssistantStore;
  readonly selfId: string | null;
  /** The session recording: whether pieces are reaching the Worker. */
  readonly recording: RecordingState;
  /** Held still: nothing from either side is heard, written down or recorded. */
  readonly paused: boolean;
  readonly setPaused: (paused: boolean) => void;
  /** Pick the tab or screen with the other side's audio. Call from a click. */
  readonly shareOtherSide: () => Promise<void>;
  readonly end: (reason?: string) => void;
  /** After Stop: "saving" while the last piece uploads, then how the
   * recording ended up. Null until Stop. */
  readonly saved: ListenSavedState | null;
  /** The newest transcript lines, oldest first (QUEUE row 324). */
  readonly lines: readonly ListenLine[];
}

export type ListenSavedState = "saving" | ListenSaved;

/** The words for a stopped session's row. */
export function savedWord(saved: ListenSavedState): string {
  switch (saved) {
    case "saving":
      return "Saving the recording";
    case "saved":
      return "Recording saved";
    case "partial":
      return "Recording saved, part of it did not upload";
    default:
      return "Stopped";
  }
}

const RECONNECT_TRIES = 8;
const SET_ON_TRIES = 3;

const IDLE: SideState = { status: "idle", track: null, message: null };

function newDeviceId(): string {
  return typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `listen-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

/** What the room-creating call said when it refused, in words for the screen. */
function createProblem(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return "Sign in again to listen.";
    if (err.code === "plan_required" || err.status === 402) return "This account's plan does not include one-off rooms.";
    return err.message;
  }
  return "Could not start. Try again.";
}

export function useListen(target: ListenTarget, me: AccountUser): ListenStore {
  const hostName = me.displayName.trim() || "Me";
  const guestName = listenGuestName(target);
  // One code for the life of the page. A new visit is a new room, so each
  // listen session is its own entry in the call history.
  const code = useMemo(() => newOnceCode((b) => crypto.getRandomValues(b)), []);
  const roomKey = `once:${code}`;

  const [phase, setPhase] = useState<ListenPhase>("starting");
  const [error, setError] = useState<string | null>(null);
  const [selfId, setSelfId] = useState<string | null>(null);
  const [mine, setMine] = useState<SideState>(IDLE);
  const [theirs, setTheirs] = useState<SideState>(IDLE);
  const [paused, setPausedState] = useState(false);
  const [recording, setRecording] = useState<RecordingState>("waiting");
  const [saved, setSaved] = useState<ListenSavedState | null>(null);
  // The words so far (QUEUE row 324): the Room's newest lines, whole each time.
  const [lines, setLines] = useState<readonly ListenLine[]>([]);
  const linesRef = useRef<readonly ListenLine[]>([]);
  const savedRef = useRef<ListenSavedState | null>(null);
  savedRef.current = saved;

  const hostSocket = useRef<RoomSocket | null>(null);
  const guestSocket = useRef<RoomSocket | null>(null);
  const hostRejoin = useRef<string | null>(null);
  const guestRejoin = useRef<string | null>(null);
  const guestDevice = useRef<string>(newDeviceId());
  const guestWelcomed = useRef(false);
  const hostWelcomed = useRef(false);
  const endedRef = useRef(false);
  // Why the session ended and what led up to it, sent with every report so the
  // record survives a tab that closes without a word (Adam, 2026-10-05: the
  // Mom call's recording stopped and nothing said why).
  const trailRef = useRef<string[]>([]);
  const reasonRef = useRef<string | null>(null);
  const note = useCallback((what: string): void => {
    const at = new Date().toLocaleTimeString("en-US", { timeZone: "America/New_York", hour12: false });
    trailRef.current = [...trailRef.current, `${at} ${what}`].slice(-40);
  }, []);
  const wantOn = useRef(true);
  const setOnTries = useRef(0);
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const [guestUp, setGuestUp] = useState(false);

  const sendHost = useCallback((message: ClientMessage) => hostSocket.current?.send(message), []);
  const sendGuest = useCallback((message: ClientMessage) => guestSocket.current?.send(message), []);
  const audioHost = useCallback((frame: ArrayBuffer) => hostSocket.current?.sendBinary(frame), []);
  const audioGuest = useCallback((frame: ArrayBuffer) => guestSocket.current?.sendBinary(frame), []);
  const pingsHost = useCallback((on: boolean) => hostSocket.current?.setPingMs(on ? PING_ASSISTANT_MS : PING_MS), []);
  const pingsGuest = useCallback((on: boolean) => guestSocket.current?.setPingMs(on ? PING_ASSISTANT_MS : PING_MS), []);

  const panel = usePanel(roomKey, sendHost);
  const hostAssistant = useAssistant(sendHost, audioHost, mine.track, pingsHost, `${roomKey}:host`);
  const guestAssistant = useAssistant(sendGuest, audioGuest, theirs.track, pingsGuest, `${roomKey}:guest`);

  // The newest copies, for handlers an open socket keeps.
  const panelIngest = useRef(panel.ingest);
  panelIngest.current = panel.ingest;
  const requestPrivate = useRef(panel.requestPrivate);
  requestPrivate.current = panel.requestPrivate;
  const hostIngest = useRef(hostAssistant.ingest);
  hostIngest.current = hostAssistant.ingest;
  const guestIngest = useRef(guestAssistant.ingest);
  guestIngest.current = guestAssistant.ingest;

  const later = useCallback((fn: () => void, ms: number) => {
    const timer = setTimeout(() => {
      timers.current.delete(timer);
      fn();
    }, ms);
    timers.current.add(timer);
  }, []);

  const fail = useCallback(
    (message: string) => {
      if (endedRef.current) return;
      reasonRef.current ??= `failed: ${message}`;
      note(`failed: ${message}`);
      setError(message);
      setPhase("failed");
    },
    [note],
  );

  // ---- seats ----------------------------------------------------------------

  const connectGuestRef = useRef<(tries: number) => void>(() => {});
  const connectHostRef = useRef<(tries: number) => void>(() => {});

  connectGuestRef.current = (tries: number) => {
    if (endedRef.current) return;
    const url = roomWsUrl("once", code, guestName, guestDevice.current, guestRejoin.current ?? undefined);
    const socket = new RoomSocket(url, {
      onMessage: (message: RoomMessage) => {
        if (guestSocket.current !== socket) return;
        if (message.type === "welcome") {
          guestRejoin.current = message.rejoinToken;
          guestWelcomed.current = true;
          note("guest seat in");
          setGuestUp(true);
          // A seat that dropped and came back must not leave the page on
          // "Reconnecting": the host seat is the one that sets it live.
          if (hostWelcomed.current) setPhase((p) => (p === "reconnecting" ? "live" : p));
          return;
        }
        guestIngest.current(message);
      },
      onClose: (event) => {
        if (endedRef.current || event.code === ROOM_CLOSE_CODES.left) return;
        if (guestSocket.current !== socket) return;
        note(`guest seat closed (${event.code}), try ${tries}`);
        guestWelcomed.current = false;
        setGuestUp(false);
        if (event.code === ROOM_CLOSE_CODES.replaced || event.code === ROOM_CLOSE_CODES.ended) {
          fail("This listen session was closed.");
          return;
        }
        if (tries >= RECONNECT_TRIES) {
          fail("Lost the connection. End this and start again.");
          return;
        }
        setPhase((p) => (p === "failed" ? p : "reconnecting"));
        later(() => connectGuestRef.current(tries + 1), Math.min(1000 * 2 ** tries, 8000));
      },
    });
    guestSocket.current = socket;
    socket.connect();
  };

  connectHostRef.current = (tries: number) => {
    if (endedRef.current) return;
    const url = roomWsUrl("once", code, hostName, getOrCreateDeviceId(), hostRejoin.current ?? undefined, getUserKey() ?? undefined, false, {
      [LISTEN_PARAM]: "1",
    });
    const socket = new RoomSocket(url, {
      onMessage: (message: RoomMessage) => {
        if (hostSocket.current !== socket) return;
        if (message.type === "welcome") {
          hostRejoin.current = message.rejoinToken;
          hostWelcomed.current = true;
          note("host seat in");
          setSelfId(message.selfId);
          requestPrivate.current();
          setPhase("live");
          // The other seat joins once this one is in, so the agenda lookup
          // sees the guest's name when the assistant is turned on.
          if (!guestSocket.current) connectGuestRef.current(0);
          return;
        }
        if (message.type === "listenLines") {
          linesRef.current = message.lines;
          setLines(message.lines);
          return;
        }
        if (panelIngest.current(message)) return;
        hostIngest.current(message);
      },
      onClose: (event) => {
        if (endedRef.current || event.code === ROOM_CLOSE_CODES.left) return;
        if (hostSocket.current !== socket) return;
        note(`host seat closed (${event.code}), try ${tries}`);
        hostWelcomed.current = false;
        if (event.code === ROOM_CLOSE_CODES.capReached) {
          fail("Calls are paused until next month. This month's free video is used up.");
          return;
        }
        if (event.code === ROOM_CLOSE_CODES.replaced || event.code === ROOM_CLOSE_CODES.ended || event.code === ROOM_CLOSE_CODES.movedAway) {
          fail("This listen session was closed.");
          return;
        }
        if (tries >= RECONNECT_TRIES) {
          fail("Lost the connection. End this and start again.");
          return;
        }
        setPhase((p) => (p === "failed" ? p : "reconnecting"));
        later(() => connectHostRef.current(tries + 1), Math.min(1000 * 2 ** tries, 8000));
      },
    });
    hostSocket.current = socket;
    socket.connect();
  };

  // ---- start ----------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    endedRef.current = false;

    const proceed = async (micPromise: Promise<MediaStreamTrack | null>): Promise<void> => {
      const mic = await micPromise;
      if (cancelled) {
        stopTrack(mic);
        return;
      }
      if (mic) {
        setMine({ status: "on", track: mic, message: null });
        mic.addEventListener("ended", () => {
          note("microphone stopped");
          setMine({ status: "lost", track: null, message: "The microphone stopped. Reload to use it again." });
        });
      } else {
        setMine({ status: "off", track: null, message: "The microphone is blocked. Allow it for this site to have your side heard." });
      }
      connectHostRef.current(0);
    };

    void (async () => {
      setMine({ status: "asking", track: null, message: null });
      const micPromise = openMicrophone();
      try {
        await api<{ code: string }>("/api/rooms/once", { method: "POST", body: { code } });
      } catch (err) {
        if (cancelled) return;
        // The code is ours alone, so "taken" means this page already made it
        // (a development remount runs this twice).
        if (err instanceof ApiError && err.code === "taken") {
          await proceed(micPromise);
          return;
        }
        stopTrack(await micPromise);
        setMine(IDLE);
        fail(createProblem(err));
        return;
      }
      await proceed(micPromise);
    })();

    return () => {
      cancelled = true;
    };
    // The room is made once per page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- assistant ------------------------------------------------------------

  // Turn Breeze on once both seats are in. The host's switch is the host's own
  // consent; the guest seat says Got it on the other side's behalf (below). A
  // Room that was restarted comes back off, so this also covers that.
  const hostState = hostAssistant.state;
  useEffect(() => {
    if (!guestUp || !hostState || hostState.on || !wantOn.current) return;
    if (setOnTries.current >= SET_ON_TRIES) return;
    setOnTries.current += 1;
    hostAssistant.setOn(true);
  }, [guestUp, hostState, hostAssistant]);
  useEffect(() => {
    if (hostState?.on) setOnTries.current = 0;
  }, [hostState?.on]);

  // ---- the other side -------------------------------------------------------

  const trackRef = useRef<MediaStreamTrack | null>(null);
  const recorderRef = useRef<ListenRecorder | null>(null);
  const mineForRecorder = useRef<MediaStreamTrack | null>(null);
  const shareOther = useCallback(async (): Promise<void> => {
    setTheirs((prev) => ({ ...prev, status: "asking", message: null }));
    const result = await shareOtherSide();
    if (endedRef.current) {
      if (result.ok) stopTrack(result.track);
      return;
    }
    if (!result.ok) {
      setTheirs((prev) => ({
        status: prev.track ? "on" : result.reason === "cancelled" ? "idle" : "off",
        track: prev.track,
        message: result.reason === "cancelled" ? null : result.message,
      }));
      return;
    }
    stopTrack(trackRef.current);
    const track = result.track;
    trackRef.current = track;
    track.addEventListener("ended", () => {
      if (trackRef.current !== track) return;
      trackRef.current = null;
      note("share stopped");
      setTheirs({ status: "lost", track: null, message: "Sharing stopped. Share again to keep listening." });
    });
    note("share started");
    setTheirs({ status: "on", track, message: null });
    // The session recording: both sides' sound and the video of the share.
    // Never blocks listening; a failure here leaves the transcript path alone.
    try {
      recorderRef.current ??= new ListenRecorder(code);
      if (mineForRecorder.current) recorderRef.current.addAudio("mine", mineForRecorder.current);
      recorderRef.current.addAudio("theirs", track);
      recorderRef.current.startWith(result.video);
      if (pausedRef.current) recorderRef.current.setPaused(true);
    } catch {
      // Not recorded.
    }
  }, [code]);

  // ---- pause ----------------------------------------------------------------

  // Pausing turns both tracks to silence at the source, so the same quiet
  // reaches the assistant's lines and the recording, and holds the recorder
  // still so the paused stretch is not in the file. The seats stay joined and
  // the room stays open.
  const pausedRef = useRef(false);
  const applyPaused = useCallback((): void => {
    const on = !pausedRef.current;
    if (mineForRecorder.current) mineForRecorder.current.enabled = on;
    if (trackRef.current) trackRef.current.enabled = on;
    recorderRef.current?.setPaused(!on);
  }, []);
  const setPaused = useCallback(
    (next: boolean): void => {
      if (endedRef.current) return;
      note(next ? "paused" : "resumed");
      pausedRef.current = next;
      setPausedState(next);
      applyPaused();
    },
    [applyPaused],
  );
  // A side shared or recovered while paused comes in quiet too.
  useEffect(() => {
    if (pausedRef.current) applyPaused();
  }, [mine.track, theirs.track, applyPaused]);

  useEffect(() => {
    const timer = setInterval(() => setRecording(recorderRef.current?.state() ?? "waiting"), 2000);
    return () => clearInterval(timer);
  }, []);

  // ---- end ------------------------------------------------------------------

  const stopEverything = useCallback((): void => {
    endedRef.current = true;
    for (const timer of timers.current) clearTimeout(timer);
    timers.current.clear();
    hostSocket.current?.leave();
    guestSocket.current?.leave();
    hostSocket.current = null;
    guestSocket.current = null;
    stopTrack(trackRef.current);
    trackRef.current = null;
  }, []);

  const mineTrackRef = useRef<MediaStreamTrack | null>(null);
  mineTrackRef.current = mine.track;
  mineForRecorder.current = mine.track;

  const endingRef = useRef(false);
  const end = useCallback((reason?: string): void => {
    if (endingRef.current) return;
    endingRef.current = true;
    reasonRef.current ??= typeof reason === "string" && reason ? reason : "stop button";
    note(`ending: ${reasonRef.current}`);
    const finish = (): void => {
      stopEverything();
      stopTrack(mineTrackRef.current);
      setMine(IDLE);
      setTheirs(IDLE);
      setPhase("ended");
    };
    // The room ends when the seats leave, and it files the recording then, so
    // the last piece of it has to be up before they do.
    const recorder = recorderRef.current;
    recorderRef.current = null;
    if (!recorder) {
      setSaved("none");
      finish();
      return;
    }
    setSaved("saving");
    void recorder.flush().then(
      (outcome) => {
        note(`recording ${outcome}`);
        setSaved(outcome);
        finish();
      },
      () => {
        setSaved("partial");
        finish();
      },
    );
  }, [stopEverything, note]);

  // His own side is recorded even when he never shares the call's audio
  // (Adam, 2026-10-05, after the Daniel Stark call: "i forgot to click share
  // with system audio"). A minute is time enough to pick the share first, which
  // is what brings the video; after that the recording starts with his
  // microphone alone, and a share made later mixes its sound in.
  useEffect(() => {
    const track = mine.track;
    if (!track) return;
    const timer = setTimeout(() => {
      if (endingRef.current) return;
      try {
        recorderRef.current ??= new ListenRecorder(code);
        const recorder = recorderRef.current;
        if (recorder.hasStarted()) return;
        recorder.addAudio("mine", track);
        recorder.startWith(null);
        if (pausedRef.current) recorder.setPaused(true);
      } catch {
        // Not recorded; the transcript path does not depend on it.
      }
    }, 60_000);
    return () => clearTimeout(timer);
  }, [mine.track, code]);

  // The indicator on his other pages (the prep page he keeps open during the
  // call): report in every few seconds, and stop when any of them asks. A
  // missed report is harmless; the indicator goes out on its own after a while.
  const phaseRef = useRef<ListenPhase>("starting");
  phaseRef.current = phase;
  const sidesRef = useRef({ mine: mine.status, theirs: theirs.status });
  sidesRef.current = { mine: mine.status, theirs: theirs.status };
  const setPausedRef = useRef(setPaused);
  setPausedRef.current = setPaused;
  useEffect(() => {
    const beat = (leaving = false): void => {
      if (endedRef.current || (phaseRef.current !== "live" && phaseRef.current !== "reconnecting")) return;
      const { mine: m, theirs: t } = sidesRef.current;
      api<ListenBeatReply>("/api/listen/beat", {
        method: "POST",
        body: {
          code,
          guest: guestName,
          mine: m,
          theirs: t,
          paused: pausedRef.current,
          trail: trailRef.current,
          ...(savedRef.current === "saving" ? { saving: true } : {}),
          ...(linesRef.current.length ? { lines: linesRef.current } : {}),
        },
        ...(leaving ? { keepalive: true } : {}),
      })
        .then((reply) => {
          if (reply.stop && !endedRef.current) end("stop from another page");
          else if (reply.pause !== undefined && reply.pause !== pausedRef.current) setPausedRef.current(reply.pause);
        })
        .catch(() => undefined);
    };
    beat();
    const timer = setInterval(beat, LISTEN_BEAT_MS);
    // The page closing or being left is the one end that cannot report itself
    // afterward, so its last report says so.
    const onHide = (): void => {
      note("page closing or being left");
      beat(true);
    };
    const onVisible = (): void => note(document.visibilityState === "hidden" ? "tab hidden" : "tab shown");
    const onOnline = (): void => note("network back");
    const onOffline = (): void => note("network lost");
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      clearInterval(timer);
      window.removeEventListener("pagehide", onHide);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [code, guestName, end, note, phase, mine.status, theirs.status, saved]);

  useEffect(() => {
    if (phase !== "ended" && phase !== "failed") return;
    api("/api/listen/end", {
      method: "POST",
      body: {
        code,
        reason: reasonRef.current ?? phase,
        trail: trailRef.current,
        ...(savedRef.current && savedRef.current !== "saving" ? { saved: savedRef.current } : {}),
      },
      keepalive: true,
    }).catch(() => undefined);
  }, [phase, code]);

  // Leaving the page, by any route, lets go of the microphone and the share.
  useEffect(
    () => () => {
      if (!endedRef.current) {
        note("screen left (the Listen page was closed or replaced)");
        api("/api/listen/end", {
          method: "POST",
          body: { code, reason: "screen left", trail: trailRef.current },
          keepalive: true,
        }).catch(() => undefined);
      }
      stopEverything();
      stopTrack(mineTrackRef.current);
    },
    [stopEverything, note, code],
  );

  // The panel's own switch keeps its word: turned off there, it stays off.
  const assistant = useMemo<AssistantStore>(
    () => ({
      ...hostAssistant,
      setOn: (on: boolean) => {
        wantOn.current = on;
        setOnTries.current = 0;
        hostAssistant.setOn(on);
      },
    }),
    [hostAssistant],
  );

  return { phase, error, hostName, guestName, mine, theirs, panel, assistant, selfId, recording, paused, setPaused, shareOtherSide: shareOther, end, saved, lines };
}
